<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import ExpectedReportRepository from '../repository/ExpectedReportRepository.js'
import { Loader, TableSkeletonLoader, Paginate, PaginateHelper } from 'dmui_components'
import ExpectedReportList from './ExpectedReportList.vue'
import ExpectedReportFilters from './ExpectedReportFilters.vue'

const PAGE_SIZE = 100

const reportsResponse = ref()
const totalCount = ref(null)
const currentPage = ref(1)
const totalPages = computed(() => Math.ceil((totalCount.value || 0) / PAGE_SIZE))
const reportItems = computed({
	get: () => reportsResponse.value?.items,
	set: (newItems) => {
		if (reportsResponse.value) {
			reportsResponse.value.items = newItems
		}
	}
})

defineProps({
	readonly: {
		type: Boolean,
		default: false
	}
})

const DEFAULT_FILTERS = {
	document_type: [],
	document_year: [],
	country_name: [],
	industry_name: [],
	fiscal_month: [],
	market_bucket: [],
	expected_date: undefined,
	status: ['expected']
}

const DEFAULT_SEARCH = {
	org_name: ''
}

const DEFAULT_MODIFIERS = {
	search: { ...DEFAULT_SEARCH },
	filters: { ...DEFAULT_FILTERS }
}

const modifiers = ref()
const isInitialized = ref(false)
const lastFetchedParams = ref(null)

const loading = ref(false)

let fetchDebounceTimer = null

async function fetchReports(fetchCount = true) {
	const paramsWithPage = { ...modifiers.value, page: currentPage.value - 1, page_size: PAGE_SIZE }
	const currentParams = JSON.stringify(paramsWithPage)
	// Skip if we already fetched with these exact parameters
	if (currentParams === lastFetchedParams.value) {
		return
	}

	loading.value = true
	lastFetchedParams.value = currentParams

	ExpectedReportRepository.fetchReportsFromApi(paramsWithPage)
		.then((response) => {
			reportsResponse.value = response
		})
		.catch((e) => console.error(e))
		.finally(() => {
			loading.value = false
		})

	if (fetchCount) {
		totalCount.value = null // Reset count while loading new data
		ExpectedReportRepository.fetchReportsCountFromApi(paramsWithPage)
			.then((response) => {
				totalCount.value = response?.total_count
			})
			.catch((e) => console.error(e))
	}
}

function onPageUpdate(page) {
	currentPage.value = page
	fetchReports(false) // Don't refetch count when just changing page
}

function debouncedFetchReports() {
	if (fetchDebounceTimer) {
		clearTimeout(fetchDebounceTimer)
	}
	fetchDebounceTimer = setTimeout(() => {
		fetchReports()
	}, 100)
}

watch(
	modifiers,
	async (newFilters) => {
		if (!isInitialized.value) return
		localStorage.setItem('expected_report_modifiers', JSON.stringify(newFilters))
		currentPage.value = 1 // Reset to first page when filters change
		debouncedFetchReports()
	},
	{ deep: true }
)

onMounted(async () => {
	const savedFilters = localStorage.getItem('expected_report_modifiers')
	if (savedFilters) {
		modifiers.value = JSON.parse(savedFilters)
	} else {
		modifiers.value = { ...DEFAULT_MODIFIERS }
	}
	// Fetch once on mount, then enable watch
	await fetchReports()
	isInitialized.value = true
})
</script>

<template>
	<Loader v-if="!reportsResponse" />
	<div v-else>
		<ExpectedReportFilters v-model:filters="modifiers.filters" :readonly="readonly" @reset:filters="modifiers.filters = { ...DEFAULT_FILTERS }" />
		<h1 class="primary-header reports-count">Found {{ totalCount !== null ? totalCount : '...' }} expected reports</h1>
		<ExpectedReportList v-model:search="modifiers.search" v-model:reports="reportItems" :readonly="readonly" @update:monitoring="fetchReports">
			<TableSkeletonLoader v-if="loading" :n-rows="20" :n-columns="7" />
		</ExpectedReportList>
		<div class="pagination-container">
			<PaginateHelper :current-page="currentPage" :total="totalCount || 0" :limit="PAGE_SIZE" />
			<Paginate :current-page="currentPage" :max-page="totalPages" @update:current-page="onPageUpdate" />
		</div>
	</div>
</template>

<style scoped lang="less">
.reports-count {
	margin-top: @medium;
}
.column-order {
	gap: @micro;
}

:deep(.column-order-icon button) {
	display: none; // TODO: can be removed once the component is updated to be able to handle search independently
}
</style>
